<template>
  <!-- Kiosk URL: same status layout as the kiosk payment screens (.ksk-pay in
       kiosk-theme2.css; App.vue gives this page the kiosk root/zoom/frame). -->
  <div v-if="isKiosk" class="ksk-err">
    <div class="ksk-pay">
      <div class="ksk-pay__icon" :class="{ 'ksk-pay__icon--warning': isRetryable }">
        <span class="material-icons-round">{{ icon }}</span>
      </div>
      <div class="ksk-pay__title">{{ isRetryable ? "Connection Problem" : "Something went wrong" }}</div>
      <div class="ksk-pay__caption">{{ message }}</div>
      <div v-if="!isRetryable" class="ksk-err__code">Error {{ errorInfo.statusCode || 404 }}</div>

      <button v-if="attemptedUrl" type="button" class="ksk-err__url" @click="copyUrl">
        <span class="ksk-err__url-body">
          <span class="ksk-err__url-label">Attempted URL</span>
          <code class="ksk-err__url-value">{{ attemptedUrl }}</code>
        </span>
        <span class="material-icons-round ksk-err__url-copy">{{ copied ? "check" : "content_copy" }}</span>
      </button>

      <KskButton class="ksk-err__btn" size="md" icon="refresh" @click="retryNow">
        {{ isRetryable ? "Try Again" : "Reload" }}
      </KskButton>
    </div>
  </div>

  <div v-else class="notfound-page">
    <div class="notfound-glow"></div>
    <div class="notfound-blob notfound-blob-a"></div>
    <div class="notfound-blob notfound-blob-b"></div>
    <div class="notfound-blob notfound-blob-c"></div>

    <div class="notfound-card">
      <div class="notfound-icon-wrap">
        <span class="material-icons-round notfound-icon">{{ icon }}</span>
      </div>

      <h1 class="notfound-title">{{ title }}</h1>
      <p class="notfound-message">{{ message }}</p>

      <button
        v-if="attemptedUrl"
        type="button"
        class="notfound-url"
        @click="copyUrl"
      >
        <span class="material-icons-round notfound-url-icon">link</span>
        <span class="notfound-url-body">
          <span class="notfound-url-label">Attempted URL</span>
          <code class="notfound-url-value">{{ attemptedUrl }}</code>
        </span>
        <span class="notfound-url-copy" :class="{ copied }">
          <span class="material-icons-round">{{ copied ? "check" : "content_copy" }}</span>
        </span>
      </button>

      <button class="notfound-btn" @click="retryNow">
        <span class="material-icons-round notfound-btn-icon">refresh</span>
        {{ isRetryable ? "Try Again" : "Reload" }}
      </button>
    </div>
  </div>
</template>

<script>
import KskButton from "@/views/kiosk/components/ui/KskButton.vue";

export default {
  name: "Error404",
  components: {
    KskButton,
  },
  data() {
    return {
      copied: false,
    };
  },
  computed: {
    errorInfo() {
      return this.$store.state.error || {};
    },
    // The failed page was a kiosk page (see App.vue isKioskErrorPage).
    isKiosk() {
      return (this.errorInfo.returnPath || "").includes("/kiosk");
    },
    isRetryable() {
      return !!this.errorInfo.retryable;
    },
    icon() {
      return this.isRetryable ? "wifi_off" : "search_off";
    },
    title() {
      if (this.isRetryable) {
        return "Connection Problem";
      }
      return this.errorInfo.statusCode ? String(this.errorInfo.statusCode) : "404";
    },
    message() {
      if (this.errorInfo.message) {
        return this.errorInfo.message;
      }
      return this.isRetryable
        ? "Unable to connect. Please check your connection."
        : "Sorry, we couldn't find the page you're looking for.";
    },
    // returnPath is always domain-less (Vue Router's to.fullPath is a bare
    // path), so there's no host to leak — but mask a domain placeholder
    // anyway so this reads unambiguously as "not the real host" if it's
    // ever screenshotted or shared for support.
    attemptedUrl() {
      if (!this.errorInfo.returnPath) {
        return "";
      }
      return `*****${this.errorInfo.returnPath}`;
    },
  },
  methods: {
    retryNow() {
      // Reloading /404 itself retries nothing — merchant_code/branch_code
      // only ever lived in the original URL, so navigate back to it (a full
      // load, not router.push, to fully re-run App.vue's checks).
      if (this.errorInfo.returnPath) {
        window.location.href = this.errorInfo.returnPath;
      } else {
        window.location.reload();
      }
    },
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.attemptedUrl);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 1500);
      } catch (e) {
        // Clipboard API unavailable/blocked — the text is already visible
        // and selectable, so there's nothing else useful to do here.
      }
    },
  },
};
</script>

<style scoped>
/* Kiosk variant: status block centred on the kiosk background. */
.ksk-err {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(var(--ksk-vh, 1vh) * 100);
  padding: var(--ksk-space-4) var(--ksk-space-3);
  background: var(--ksk-background);
}
.ksk-err .ksk-pay {
  max-width: 640px;
}
.ksk-err__code {
  margin-top: 4px;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ksk-text-secondary);
}
.ksk-err__url {
  display: flex;
  align-items: center;
  gap: var(--ksk-space-2);
  width: 100%;
  max-width: 560px;
  margin-top: var(--ksk-space-4);
  padding: 12px var(--ksk-space-2);
  border: 1px solid var(--ksk-border);
  border-radius: 16px;
  background: var(--ksk-surface);
  text-align: left;
  cursor: pointer;
}
.ksk-err__url-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ksk-err__url-label {
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ksk-text-secondary);
}
.ksk-err__url-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  line-height: 22px;
  color: var(--ksk-text);
}
.ksk-err__url-copy {
  flex: none;
  font-size: 24px;
  color: var(--ksk-text-secondary);
}
.ksk-err__btn {
  min-width: 280px;
  margin-top: var(--ksk-space-4);
}

.notfound-page {
  --nf-accent: #f9dd4b;
  --nf-accent-dark: color-mix(in srgb, var(--nf-accent) 80%, black);
  --nf-accent-soft: color-mix(in srgb, var(--nf-accent) 15%, white);
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f6f6fb;
  overflow: hidden;
}

.notfound-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    60% 50% at 50% 38%,
    color-mix(in srgb, var(--nf-accent) 12%, transparent),
    transparent 70%
  );
  pointer-events: none;
}

.notfound-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.32;
  pointer-events: none;
}

.notfound-blob-a {
  width: 380px;
  height: 380px;
  top: -130px;
  left: -110px;
  background: var(--nf-accent);
}

.notfound-blob-b {
  width: 320px;
  height: 320px;
  bottom: -110px;
  right: -90px;
  background: color-mix(in srgb, var(--nf-accent) 55%, white);
}

.notfound-blob-c {
  width: 240px;
  height: 240px;
  top: 42%;
  left: 58%;
  background: color-mix(in srgb, var(--nf-accent) 35%, white);
  opacity: 0.22;
}

.notfound-card {
  position: relative;
  width: 100%;
  max-width: 440px;
  text-align: center;
  padding: 56px 40px;
  background: #ffffff;
  border-radius: 28px;
  box-shadow: 0 30px 70px -24px color-mix(in srgb, var(--nf-accent) 28%, transparent),
    0 4px 16px rgba(17, 17, 17, 0.05);
  animation: notfound-rise 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.notfound-icon-wrap {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto 28px;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--nf-accent-soft), #ffffff);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--nf-accent) 12%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
}

.notfound-icon {
  font-size: 46px;
  color: var(--nf-accent);
}

.notfound-title {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #16161d;
  margin: 0 0 12px;
}

.notfound-message {
  font-size: 15px;
  color: #6b6b78;
  line-height: 1.6;
  margin: 0 0 24px;
}

.notfound-url {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 0 0 28px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f7f7fb;
  border: 1px solid #ececf3;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.notfound-url:hover {
  background: #f0f0f7;
  border-color: #e0e0ec;
}

.notfound-url-icon {
  flex: none;
  font-size: 18px;
  color: #a3a3b0;
}

.notfound-url-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.notfound-url-label {
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #adadb8;
}

.notfound-url-value {
  display: block;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12.5px;
  color: #4b4b58;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notfound-url-copy {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a3a3b0;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.notfound-url-copy .material-icons-round {
  font-size: 16px;
}

.notfound-url-copy.copied {
  background: #dcfce7;
  color: #16a34a;
}

.notfound-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 999px;
  min-height: 50px;
  padding: 0 36px;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, var(--nf-accent), var(--nf-accent-dark));
  box-shadow: 0 10px 24px -8px color-mix(in srgb, var(--nf-accent) 55%, transparent);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.notfound-btn-icon {
  font-size: 19px;
}

.notfound-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px -8px color-mix(in srgb, var(--nf-accent) 60%, transparent);
}

.notfound-btn:active {
  transform: translateY(0) scale(0.97);
  box-shadow: 0 6px 16px -6px color-mix(in srgb, var(--nf-accent) 55%, transparent);
}

@keyframes notfound-rise {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 420px) {
  .notfound-card {
    padding: 44px 24px;
  }
}
</style>
